import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useChatSocket } from '../../hooks/useChatSocket';
import {
  MessageSquare,
  BarChart2,
  Loader2,
  Copy,
  Download,
  RefreshCw,
  Mail,
  X,
  ChevronDown,
  PanelLeftClose,
  ChevronUp,
  FileText,
  Edit,
  Share2,
  Info,
  ExternalLink,
  ShieldOff,
  Printer,
} from 'lucide-react';
import { useSimulationSocket } from '@/hooks/useSimulationSocket';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../components/ui/dialog.jsx';
import { CustomDialogClose } from '../../components/ui/dialog-custom.jsx';
import html2canvas from 'html2canvas';
import {
  validateBase64Image,
  createPlaceholderDataUrl,
  compressBase64Image,
} from '../../utils/imageValidation';
import { apiErrorMessage } from '../../utils/apiErrorMessage';
import {
  ALLOWED_IMAGE_MIME_TYPES,
  UNSUPPORTED_FORMAT_MESSAGE,
} from '../simulationUseCase/useCaseForms/conceptImageFormats';

// Import new sub-components
import ExportModal from './components/ExportModal';
import ChatInterface from './components/ChatInterface';
import SimulationAnalysisPanel from './components/SimulationAnalysisPanel';
import EditRerunModal from './components/EditRerunModal';
import SimulationFailedPanel from './components/SimulationFailedPanel';
import ShareModal from './ShareModal';
import { zIndexClasses } from '../../config/zIndex';
import { useSimulationTimeEstimate } from '../../hooks/useSimulationTimeEstimate';
import {
  getSimChatContext,
  applyOptimisticSimQuery,
  type SimChatContext,
} from '../marketplace/marketplaceApi';
import { isDisplayableChatMessage } from './chatHistoryFilter';
import { rerunSimulation } from '../../utils/simulationApi';
import { simulationDetailFailure } from '../../utils/simulationFailure';

const API_URL = import.meta.env.VITE_API_URL || '';

// Query budget for the marketplace chat-with-simulation (#33/#45).
// Fallback only — the real cap comes from the server (`queries_allowed` on
// /sim-chat-context). Used until that resolves, or if the request fails.
const MARKETPLACE_SIM_CHAT_QUERY_CAP = 5;

// Fallback for a missed socket event while a simulation is unfinished.
const STATUS_POLL_INTERVAL_MS = 15_000;
const HISTORY_PATH = '/analysis';

interface Persona {
  id: number;
  name: string;
  data: any;
}

interface SimulationData {
  id: number;
  name?: string;
  audience_id: number;
  simulation_response: string;
  failure_message?: string | null;
  source_model: string;
  optimization_response: string;
  status: string;
  personas: Persona[];
  created_at: string;
  updated_at: string;
  content?: string;
  segments?: {
    id: number;
    name: string;
  }[];
  num_tabs?: number;
}

interface ContentData {
  [key: string]: unknown;
  audience_name?: string;
  name?: string;
  goal?: string;
  task?: string;
  content_type?: string;
  segment_ids?: number[];
  persona_filters?: Record<string, Record<string, unknown>>;
  images?: string[];
  current_logo?: string;
  questions?: any[];
  num_questions?: number;
}

interface SimulationResultsContentProps {
  simulationId: string | number;
  onError?: (error: string) => void;
  defaultChatOpen?: boolean;
  isPublicView?: boolean;
  isAdminView?: boolean;
  /** RLHF rating workbench: `simulationId` carries the ASSIGNMENT id, not a simulation id.
   *  Reads through the assignment-scoped endpoint (raters don't own these simulations) and
   *  drops the live socket, chat and sharing tools — a rater only reads a finished result. */
  isRatingView?: boolean;
  /** Root-relative API path to read the report from, overriding the endpoint `isRatingView`
   *  implies (isPublicView/isAdminView still take precedence). Used by the pairwise comparison
   *  page, where each pane reads one side of a pair. */
  ratingEndpointPath?: string;
  /** Stack the inputs above the report instead of beside it. The side-by-side inputs column is a
   *  fixed 380px keyed to VIEWPORT width, which leaves almost nothing for the report when the
   *  component is rendered into a half-width pane. */
  stackInputs?: boolean;
  hideChat?: boolean;
  hideHeader?: boolean;
  /** Marketplace embed: hide chat, the redundant analysis tab, and the
   *  copy/print/rerun/edit-&-rerun tools — keep only Share + Simulation Inputs. */
  marketplaceView?: boolean;
  /** Marketplace study behind this simulation. Lets the chat cap read its
   *  server-authoritative used-count so it survives reloads (#33/#45). */
  marketplaceStudyId?: number;
  /** The signed-in account email. In the marketplace the study already emails this
   *  address by default, so the "get notified" card reassures ("we'll email <you>")
   *  and only offers to add a DIFFERENT address — instead of asking for an email the
   *  system already has. */
  notifyDefaultEmail?: string;
}

// Helper function at the top level
function blobToDataURL(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

// The marketplace results page is reachable from a phone; the enterprise app is not.
// Below md the chat/report split stacks instead of squeezing into two useless columns.
// Stacked on a phone the two panes each take a slice of the viewport: the chat is a
// little shorter than the report, so opening it still leaves the report visible
// underneath rather than filling the screen.
const MOBILE_CHAT_HEIGHT = 'h-[65vh]';
// On a phone the report is NOT a scroll box — it flows with the page, so the whole
// document scrolls. Capping it made the report a keyhole inside the viewport.
const MOBILE_REPORT_FLOW = 'h-auto overflow-visible';

/**
 * Persists the rating view's input-panel collapse choice. Stores the COLLAPSED state rather than
 * the open one, so an absent or unreadable value naturally means "expanded" — the safe default,
 * since a rater must see the brief.
 */
const RATING_INPUTS_COLLAPSED_KEY = 'bluepill.rating.inputsCollapsed';

const SimulationResultsContent: React.FC<SimulationResultsContentProps> = React.memo(
  ({
    simulationId,
    onError,
    defaultChatOpen = false,
    isPublicView = false,
    isAdminView = false,
    isRatingView = false,
    ratingEndpointPath,
    stackInputs = false,
    hideChat = false,
    hideHeader = false,
    marketplaceView = false,
    marketplaceStudyId,
    notifyDefaultEmail,
  }): React.ReactElement => {
    const [simulation, setSimulation] = useState<SimulationData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState<'simulation' | 'advanced'>('simulation');
    const [chatTab, setChatTab] = useState<'simulation' | 'persona'>('simulation');
    const [selectedPersona, setSelectedPersona] = useState<number | null>(null);
    const [chatMessage, setChatMessage] = useState('');
    // Marketplace chat-with-simulation is the point of the page, so open it by
    // Marketplace opens on the RESULTS, chat COLLAPSED — the report is what the user paid
    // for, and an auto-open chat is easily confused with the platform's other chat. A
    // prominent gradient "Chat with results" CTA (below) is the obvious way in. The private
    // platform view keeps its #33 default-open behaviour.
    const [isChatCollapsed, setIsChatCollapsed] = useState(
      marketplaceView ? true : !defaultChatOpen,
    );
    const [simulationStatus, setSimulationStatus] = useState<
      'pending' | 'running' | 'completed' | 'partial' | 'failed'
    >('pending');
    const [currentStep, setCurrentStep] = useState<string>('Initializing simulation...');
    const [showTooltip, setShowTooltip] = useState('');
    const [copied, setCopied] = useState(false);
    const [exportStatus, setExportStatus] = useState<'idle' | 'exporting' | 'success' | 'error'>(
      'idle',
    );
    const [exportError, setExportError] = useState('');
    const [showExportModal, setShowExportModal] = useState(false);
    const [exportEmailStatus, setExportEmailStatus] = useState<
      'idle' | 'sending' | 'success' | 'error'
    >('idle');
    const [exportEmailError, setExportEmailError] = useState('');
    const [rerunLoading, setRerunLoading] = useState(false);
    const [rerunError, setRerunError] = useState('');
    const navigate = useNavigate();

    // State for collapsible cards in content summary
    const [expandedCards, setExpandedCards] = useState<Record<string, boolean>>({
      details: true,
      audience: false,
      additional: false,
      personas: false,
      analysis: false,
      questions: true,
      norms: true,
    });

    // Rating view shows the inputs inline above the results: a rater has to read what was asked
    // before they can judge the answer, so it must not be behind a click. The toolbar holding the
    // "Simulation Inputs" button is hidden in this view, so a popover would be unreachable anyway.
    const inputsInline = isRatingView;
    // Deliberately NOT isDetailsDropdownOpen — that one is wired to Escape and outside-click, either
    // of which would collapse the rater's inputs on a stray click mid-review.
    //
    // Seeded from localStorage so the choice survives moving between assignments and reloading.
    // A rater works a long queue; re-collapsing the panel on every item is a tax on every item.
    const [isInlineInputsOpen, setIsInlineInputsOpen] = useState<boolean>(() => {
      if (!isRatingView) return true;
      try {
        return window.localStorage.getItem(RATING_INPUTS_COLLAPSED_KEY) !== 'true';
      } catch {
        // Private mode / storage disabled — fall back to expanded rather than breaking the page.
        return true;
      }
    });

    const toggleInlineInputs = useCallback(() => {
      setIsInlineInputsOpen((wasOpen) => {
        const nowOpen = !wasOpen;
        try {
          window.localStorage.setItem(RATING_INPUTS_COLLAPSED_KEY, String(!nowOpen));
        } catch {
          /* preference is a convenience; never block the toggle on storage */
        }
        return nowOpen;
      });
    }, []);

    // State for dropdown
    const [isDetailsDropdownOpen, setIsDetailsDropdownOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const dropdownButtonRef = useRef<HTMLButtonElement>(null);
    const loadedSimIdRef = useRef<string | null>(null);

    // State for image modal
    const [selectedImage, setSelectedImage] = useState<{ src: string; alt: string } | null>(null);

    // State for export preparation
    const [isPreparingExport, setIsPreparingExport] = useState(false);

    // PDF generation state
    const [cachedPdfUrl, setCachedPdfUrl] = useState<string | null>(null);
    const [pdfGenerating, setPdfGenerating] = useState(false);
    const [pdfCacheKey, setPdfCacheKey] = useState<string | null>(null);
    const [pdfCheckInterval, setPdfCheckInterval] = useState<NodeJS.Timeout | null>(null);
    const [currentEmailRecipient, setCurrentEmailRecipient] = useState<string>('');

    // Media upload states
    const [mediaFiles, setMediaFiles] = useState<File[]>([]);
    const [mediaPreviewUrls, setMediaPreviewUrls] = useState<string[]>([]);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Media viewer modal states
    const [viewerMedia, setViewerMedia] = useState<{ type: 'image' | 'video'; src: string } | null>(
      null,
    );

    // Upload progress state
    const [isUploadingMedia, setIsUploadingMedia] = useState(false);

    // Add state for email notification modal
    const [showEmailModal, setShowEmailModal] = useState(false);
    const [emailNotificationStatus, setEmailNotificationStatus] = useState<
      'idle' | 'sending' | 'success' | 'error'
    >('idle');
    const [emailNotificationError, setEmailNotificationError] = useState('');
    const [notificationEmail, setNotificationEmail] = useState('');

    // State for Edit & Rerun modal
    const [showEditRerunModal, setShowEditRerunModal] = useState(false);

    // State for sharing
    const [isShareModalOpen, setIsShareModalOpen] = useState(false);
    const [shareUrl, setShareUrl] = useState<string | null>(null);
    const [isSharing, setIsSharing] = useState(false);
    const [isPublicShared, setIsPublicShared] = useState(false);
    const [isUnsharing, setIsUnsharing] = useState(false);
    const [shareDropdownOpen, setShareDropdownOpen] = useState(false);
    const shareDropdownRef = useRef<HTMLDivElement>(null);

    // State for print mode (browser print to PDF)
    const [isPrintMode, setIsPrintMode] = useState(false);
    const [isPrintPreparing, setIsPrintPreparing] = useState(false);

    const isSimulationRunningForEstimate =
      simulationStatus === 'running' || simulationStatus === 'pending';
    const { estimate: estimatedTime, remainingMinutes } = useSimulationTimeEstimate(
      !isPublicView && !isAdminView && !isRatingView ? simulationId : null,
      isSimulationRunningForEstimate,
    );

    // State for notifications and confirmations
    const [notification, setNotification] = useState<{
      message: string;
      type: 'success' | 'error';
    } | null>(null);
    const [confirmDialog, setConfirmDialog] = useState<{
      message: string;
      onConfirm: () => void;
    } | null>(null);

    // WebSocket chat functionality
    const {
      chatState,
      sendMessage: sendChatMessage,
      addOptimisticMessage,
      removeOptimisticMessage,
      clearChat,
      hydrateMessages,
      isConnected: isChatConnected,
    } = useChatSocket({
      // Rating view passes an ASSIGNMENT id, not a simulation id, and hides chat entirely —
      // connecting would open a channel keyed to an unrelated simulation.
      autoConnect: !isRatingView,
      chatType: chatTab,
      simulationId: simulationId ? parseInt(String(simulationId)) : undefined,
      personaId: selectedPersona || undefined,
      chatId: `content_${chatTab}_${String(simulationId)}_${selectedPersona || 'sim'}`,
      onMessageReceived: (data) => {},
      onResponseStarted: (data) => {},
      onResponseCompleted: (data) => {},
      onResponseFailed: (data) => {
        console.error('Chat response failed:', data);
        setError(apiErrorMessage(data, 'Chat response failed'));
      },
    });

    // Load prior simulation-chat history on a fresh page load / return visit, so the user
    // sees the earlier conversation for reference (chat is persisted server-side). Defensive
    // by design: any failure — not-owned, no history yet, network — is a SILENT no-op, so the
    // chat just starts empty exactly as before. Only seeds the SIMULATION chat; hydrateMessages
    // itself no-ops once the chat already has messages, so it never clobbers a live session.
    useEffect(() => {
      if (isRatingView || isPublicView || chatTab !== 'simulation') return;
      const simId = simulationId ? parseInt(String(simulationId)) : undefined;
      if (!simId || Number.isNaN(simId)) return;
      let cancelled = false;
      (async () => {
        try {
          const res = await fetch(`${API_URL}/chat/simulation/${simId}`, {
            credentials: 'include',
          });
          if (!res.ok) return; // 404 = no history yet / not owned → leave chat empty
          const data = await res.json();
          const raw = Array.isArray(data?.messages) ? data.messages : [];
          const mapped = raw
            .map((m: any, i: number) => {
              // Media persisted on the turn (ownership-guarded /api/simulation-images/serve paths
              // from the backend) — rehydrate so uploaded images/videos reappear after a reload,
              // same shape ChatInterface renders live. Resolve relative serve paths against API_URL;
              // leave absolute URLs and base64 data-URI fallbacks as-is.
              const toUrl = (u: string) =>
                u.startsWith('http') || u.startsWith('data:') ? u : `${API_URL}${u}`;
              const images: string[] = Array.isArray(m.images)
                ? m.images.filter(Boolean).map(toUrl)
                : [];
              const videos: string[] = Array.isArray(m.videos)
                ? m.videos.filter(Boolean).map(toUrl)
                : [];
              const hasMedia = images.length > 0 || videos.length > 0;
              return {
                id: `history-${simId}-${i}`,
                role: m.role || (m.sender === 'user' ? 'user' : 'sim'),
                content: typeof m.content === 'string' ? m.content : m.text || m.message || '',
                timestamp: m.timestamp || '',
                chat_type: 'simulation' as const,
                ...(hasMedia && { media: { images, videos } }),
              };
            })
            // Keep genuine chat turns only. The simulation persists its own internals under this
            // same chat row — a "Personas: [...]" prompt seed and the raw survey_results/analysis
            // JSON response — which are not conversation and can each be hundreds of KB; hydrating
            // those dumps ~745KB of raw text into message bubbles and freezes the page (the
            // /analysis hang). Keep a turn when its content is a displayable message OR it carries
            // media (a media-only turn is valid and its content wouldn't be seed text).
            .filter(
              (m: { content: string; media?: unknown }) =>
                isDisplayableChatMessage(m.content) || !!m.media,
            );
          if (!cancelled && mapped.length) hydrateMessages(mapped);
        } catch {
          /* no-op: chat starts empty, unchanged from before */
        }
      })();
      return () => {
        cancelled = true;
      };
      // hydrateMessages is idempotent (seeds only when empty), so it need not be a dep.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [simulationId, chatTab, isRatingView, isPublicView]);

    // WebSocket integration for real-time simulation updates
    const shouldConnectWebSocket =
      !isRatingView &&
      (simulation?.simulation_response === null || simulation?.simulation_response === undefined);
    const { isConnected } = useSimulationSocket({
      onSimulationCompleted: (data) => {
        if (data.simulation_id === Number(simulationId)) {
          fetchSimulationStatus();
        }
      },
      onSimulationFailed: (data) => {
        if (data.simulation_id === Number(simulationId)) {
          fetchSimulationStatus();
        }
      },
      autoConnect: shouldConnectWebSocket,
    });

    // Memoized callback functions
    const handleToggleDetailsDropdown = useCallback(() => {
      setIsDetailsDropdownOpen((prev) => !prev);
    }, []);

    const handleOpenExportModal = useCallback(() => {
      setShowExportModal(true);
    }, []);

    const handleCloseExportModal = useCallback(() => {
      setShowExportModal(false);
      setExportStatus('idle');
      setExportError('');
      setExportEmailStatus('idle');
      setExportEmailError('');
      setCurrentEmailRecipient('');
    }, []);

    const handleImageClick = useCallback((src: string, alt: string) => {
      setSelectedImage({ src, alt });
    }, []);

    const handleCloseImageModal = useCallback(() => {
      setSelectedImage(null);
    }, []);

    const handleViewMedia = useCallback((type: 'image' | 'video', src: string) => {
      setViewerMedia({ type, src });
    }, []);

    const handleCloseViewerMedia = useCallback(() => {
      setViewerMedia(null);
    }, []);

    const handleToggleChat = useCallback(() => {
      setIsChatCollapsed((prev) => !prev);
    }, []);

    const handleRemoveMedia = useCallback(
      (index: number) => {
        if (index === -1) {
          // Clear all
          mediaPreviewUrls.forEach((url) => URL.revokeObjectURL(url));
          setMediaFiles([]);
          setMediaPreviewUrls([]);
        } else {
          setMediaFiles((prev) => prev.filter((_, i) => i !== index));
          setMediaPreviewUrls((prev) => prev.filter((_, i) => i !== index));
        }
      },
      [mediaPreviewUrls],
    );

    const handleUploadClick = useCallback(() => {
      fileInputRef.current?.click();
    }, []);

    const handleChatTabChange = useCallback((tab: 'simulation' | 'persona') => {
      setChatTab(tab);
    }, []);

    const handleActiveTabChange = useCallback((tab: 'simulation' | 'advanced') => {
      setActiveTab(tab);
    }, []);

    const handleCopyToClipboard = useCallback(async () => {
      if (!simulation?.simulation_response) return;
      await navigator.clipboard.writeText(simulation.simulation_response);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    }, [simulation]);

    const handleRerunSimulation = useCallback(async () => {
      if (!simulation?.id) return;
      setRerunLoading(true);
      setRerunError('');
      try {
        // Through the shipped `rerunSimulation`, which already reads `upgrade_required` and the message out of the
        // NESTED `detail` the route sends. The raw fetch here threw a flat 'Failed to rerun simulation' for every
        // non-OK response, so a spent simulation quota was reported as a failure to rerun rather than as the limit
        // it is — and the server's own sentence naming the limit was discarded.
        const data = await rerunSimulation(simulation.id);
        if (!data.simulation_id) throw new Error('No new simulation id returned');
        navigate(`/simulation-results/${data.simulation_id}`);
      } catch (err: any) {
        setRerunError(err?.message || 'Failed to rerun simulation');
      } finally {
        setRerunLoading(false);
      }
    }, [simulation, navigate]);

    const handleShare = async () => {
      setIsSharing(true);
      try {
        const response = await fetch(`${API_URL}/simulations/${simulationId}/share`, {
          method: 'POST',
          credentials: 'include',
        });
        if (!response.ok) throw new Error('Failed to generate share link');

        const data = await response.json();
        setShareUrl(data.share_url);
        setIsPublicShared(true);
        setIsShareModalOpen(true);
      } catch (err) {
        console.error('Error sharing simulation:', err);
        setNotification({ message: 'Failed to generate share link', type: 'error' });
        setTimeout(() => setNotification(null), 3000);
      } finally {
        setIsSharing(false);
      }
    };

    const handleUnshare = async () => {
      setConfirmDialog({
        message: 'Make this simulation private? The share link will stop working.',
        onConfirm: async () => {
          setConfirmDialog(null);
          setIsUnsharing(true);
          try {
            const response = await fetch(`${API_URL}/simulations/${simulationId}/share`, {
              method: 'DELETE',
              credentials: 'include',
            });
            if (!response.ok) throw new Error('Failed to unshare simulation');

            setIsPublicShared(false);
            setShareUrl(null);
            setNotification({ message: 'Simulation is now private', type: 'success' });
            setTimeout(() => setNotification(null), 3000);
          } catch (err) {
            console.error('Error unsharing simulation:', err);
            setNotification({ message: 'Failed to make simulation private', type: 'error' });
            setTimeout(() => setNotification(null), 3000);
          } finally {
            setIsUnsharing(false);
          }
        },
      });
    };

    // Handle print to PDF using browser's native print dialog
    const handlePrintToPdf = async () => {
      setIsPrintPreparing(true);
      setIsPrintMode(true);
      setIsShareModalOpen(false);

      // Add print-mode-active class to body for CSS targeting
      document.body.classList.add('print-mode-active');

      // Wait for React to update DOM and sections to expand
      // Using longer delay to ensure all sections expand properly
      await new Promise((resolve) => setTimeout(resolve, 800));

      // Set up afterprint handler to reset state
      const cleanupPrintMode = () => {
        setIsPrintMode(false);
        setIsPrintPreparing(false);
        document.body.classList.remove('print-mode-active');
      };

      const handleAfterPrint = () => {
        cleanupPrintMode();
        window.removeEventListener('afterprint', handleAfterPrint);
      };

      // Fallback timeout in case afterprint doesn't fire (some browsers)
      const printTimeout = setTimeout(() => {
        cleanupPrintMode();
        window.removeEventListener('afterprint', handleAfterPrint);
      }, 60000); // 60 second timeout

      // Add cleanup for the timeout when afterprint fires
      const wrappedHandler = () => {
        clearTimeout(printTimeout);
        cleanupPrintMode();
        window.removeEventListener('afterprint', wrappedHandler);
      };
      window.addEventListener('afterprint', wrappedHandler);

      // Trigger native print dialog
      window.print();
    };

    const toggleCard = useCallback((cardName: string) => {
      setExpandedCards((prev) => ({
        ...prev,
        [cardName]: !prev[cardName],
      }));
    }, []);

    // What's loaded is identified by BOTH the id and the endpoint. The pairwise page passes the
    // same pair id to both of its panes and distinguishes them only by ratingEndpointPath, so the
    // id alone is not a complete identity for "which report is on screen". Today each pane also
    // carries a distinct React key, which independently forces a remount — this key just means the
    // fetch no longer depends on that remount to be correct.
    const loadKey = `${simulationId ?? ''}::${ratingEndpointPath ?? ''}`;
    // A status answer for a report no longer on screen must not overwrite the current one.
    const activeLoadKeyRef = useRef(loadKey);

    useEffect(() => {
      activeLoadKeyRef.current = loadKey;
      // Skip re-fetch if this exact report is already loaded (e.g. inline report remount)
      if (simulationId && loadedSimIdRef.current === loadKey) return;

      // Reset state when simulationId changes
      loadedSimIdRef.current = null;
      setSimulation(null);
      setLoading(true);
      setError(null);
      clearChat();
      setSelectedPersona(null);
      setActiveTab('simulation');
      setChatTab('simulation');
      setMediaFiles([]);
      setMediaPreviewUrls([]);
      setIsUploadingMedia(false);
      // Reset share state too — it's keyed to the specific simulation. Without this, switching
      // simulations (the component instance is reused, not remounted) left the previous study's
      // share link/flag in place, so the Share modal showed the OLD simulation's link.
      setIsShareModalOpen(false);
      setShareUrl(null);
      setIsPublicShared(false);
      setShareDropdownOpen(false);

      if (!simulationId) {
        const errorMsg = 'No simulation ID provided';
        setError(errorMsg);
        if (onError) onError(errorMsg);
        setLoading(false);
        return;
      }

      fetchSimulationStatus(true);
    }, [loadKey]); // eslint-disable-line react-hooks/exhaustive-deps

    // Clean up media preview URLs on unmount
    useEffect(() => {
      return () => {
        mediaPreviewUrls.forEach((url) => {
          if (url.startsWith('blob:')) {
            URL.revokeObjectURL(url);
          }
        });
      };
    }, [mediaPreviewUrls]);

    // Reset upload state when typing starts
    useEffect(() => {
      if (chatState.isTyping || chatState.isSending) {
        const timer = setTimeout(() => {
          setIsUploadingMedia(false);
        }, 500);
        return () => clearTimeout(timer);
      }
    }, [chatState.isTyping, chatState.isSending]);

    // Cleanup interval on unmount
    useEffect(() => {
      return () => {
        if (pdfCheckInterval) {
          clearInterval(pdfCheckInterval);
        }
      };
    }, [pdfCheckInterval]);

    // Clear PDF cache when simulation changes
    useEffect(() => {
      setCachedPdfUrl(null);
      setPdfCacheKey(null);
      setPdfGenerating(false);

      if (pdfCheckInterval) {
        clearInterval(pdfCheckInterval);
        setPdfCheckInterval(null);
      }
    }, [simulationId]);

    // Click outside handler for dropdown
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        // Check if click is outside both the dropdown and the button
        const isOutsideDropdown =
          dropdownRef.current && !dropdownRef.current.contains(event.target as Node);
        const isOutsideButton =
          dropdownButtonRef.current && !dropdownButtonRef.current.contains(event.target as Node);

        if (isOutsideDropdown && isOutsideButton) {
          setIsDetailsDropdownOpen(false);
        }
      };

      if (isDetailsDropdownOpen) {
        document.addEventListener('mousedown', handleClickOutside);
      }

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [isDetailsDropdownOpen]);

    // Click outside handler for share dropdown
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (shareDropdownRef.current && !shareDropdownRef.current.contains(event.target as Node)) {
          setShareDropdownOpen(false);
        }
      };

      if (shareDropdownOpen) {
        document.addEventListener('mousedown', handleClickOutside);
      }

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [shareDropdownOpen]);

    // Close modal on Escape key
    useEffect(() => {
      const handleEscape = (event: KeyboardEvent) => {
        if (event.key === 'Escape' && isDetailsDropdownOpen) {
          setIsDetailsDropdownOpen(false);
        }
        if (event.key === 'Escape' && shareDropdownOpen) {
          setShareDropdownOpen(false);
        }
      };

      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }, [isDetailsDropdownOpen, shareDropdownOpen]);

    const fetchSimulationStatus = async (initialFetch = false, isBackgroundPoll = false) => {
      if (!simulationId) return;
      const requestedLoadKey = loadKey;

      try {
        // Use different endpoint based on view type
        let endpoint: string;
        if (isPublicView) {
          endpoint = `${API_URL}/simulations/public/${simulationId}`;
        } else if (isAdminView) {
          endpoint = `${API_URL}/admin/free-tier/simulations/${simulationId}`;
        } else if (ratingEndpointPath) {
          endpoint = `${API_URL}${ratingEndpointPath}`;
        } else if (isRatingView) {
          endpoint = `${API_URL}/rlhf/assignments/${simulationId}/simulation`;
        } else {
          endpoint = `${API_URL}/simulations/${simulationId}`;
        }

        const response = await fetch(endpoint, {
          credentials: isPublicView ? 'omit' : 'include', // No cookies for public
        });

        if (!response.ok) {
          throw new Error('Failed to fetch simulation status');
        }

        const data = await response.json();
        if (requestedLoadKey !== activeLoadKeyRef.current) return;
        setSimulation(data);

        // Track if simulation is public (for showing share/unshare button) - not for admin view
        if (!isPublicView && !isAdminView && !isRatingView && data.is_public) {
          setIsPublicShared(true);
          // Build share URL for modal
          const frontendUrl = window.location.origin;
          setShareUrl(`${frontendUrl}/simulation/public/${data.share_token}`);
        }

        if (simulationDetailFailure(data)) {
          setSimulationStatus('failed');
          setLoading(false);
          loadedSimIdRef.current = loadKey;
        } else if (data.simulation_response) {
          const hasOptimizationResponse =
            data.optimization_response && data.optimization_response.trim() !== '';
          if (hasOptimizationResponse) {
            setSimulationStatus('completed');
          } else {
            setSimulationStatus('partial');
          }
          setLoading(false);
          loadedSimIdRef.current = loadKey;
        } else {
          setSimulationStatus('running');
          setCurrentStep(getSimulationStep(data.status));
        }
      } catch (err) {
        console.error('Error fetching simulation status:', err);
        if (isBackgroundPoll || requestedLoadKey !== activeLoadKeyRef.current) return;

        // Better error message based on view type
        if (isPublicView) {
          setError(
            'This simulation is no longer publicly available. The owner may have made it private.',
          );
        } else if (isAdminView) {
          setError('Failed to load simulation. The simulation may not exist.');
        } else {
          setError('Failed to load simulation status. Please try again.');
        }

        setLoading(false);
      }
    };

    useEffect(() => {
      const isUnfinished = simulationStatus === 'pending' || simulationStatus === 'running';
      if (!simulationId || error || !isUnfinished) return;
      const timer = setInterval(() => fetchSimulationStatus(false, true), STATUS_POLL_INTERVAL_MS);
      return () => clearInterval(timer);
    }, [simulationStatus, loadKey, error]); // eslint-disable-line react-hooks/exhaustive-deps

    const getSimulationStep = (status: string): string => {
      switch (status) {
        case 'initializing':
          return 'Initializing simulation...';
        case 'segment_processing':
          return 'Processing audience segments...';
        case 'generating_personas':
          return 'Generating audience profiles...';
        case 'running_simulation':
          return 'Running simulation with profiles...';
        case 'analyzing_results':
          return 'Analyzing simulation results...';
        case 'optimizing':
          return 'Optimizing recommendations...';
        case 'complete':
          return 'Simulation complete!';
        default:
          return 'Processing...';
      }
    };

    const parseContentField = (): ContentData => {
      if (!simulation?.content) {
        return {};
      }

      try {
        const parsed = JSON.parse(simulation.content) as ContentData;
        return parsed;
      } catch (error) {
        console.error('Error parsing content field:', error);
        return {};
      }
    };

    // Media handling functions
    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (!files) return;

      const validFiles: File[] = [];
      const previewUrls: string[] = [];

      Array.from(files).forEach((file) => {
        if (file.size > 20 * 1024 * 1024) {
          setNotification({ message: `File ${file.name} exceeds 20MB limit`, type: 'error' });
          setTimeout(() => setNotification(null), 3000);
          return;
        }

        const isVideo = file.type.startsWith('video/');
        // Images must be PNG/JPEG — the same contract the concept/packaging uploads enforce
        // (ALLOWED_IMAGE_MIME_TYPES) and the only formats the vision backend accepts. A bare
        // `image/*` accept let SVG/GIF/etc. through the picker, which then failed downstream.
        const isImage = ALLOWED_IMAGE_MIME_TYPES.includes(file.type);

        if (!isImage && !isVideo) {
          const looksLikeImage = file.type.startsWith('image/');
          setNotification({
            message: looksLikeImage
              ? `${file.name}: ${UNSUPPORTED_FORMAT_MESSAGE}`
              : `File ${file.name} is not supported`,
            type: 'error',
          });
          setTimeout(() => setNotification(null), 3000);
          return;
        }

        if (mediaFiles.length + validFiles.length >= 4) {
          setNotification({ message: 'Maximum 4 files allowed', type: 'error' });
          setTimeout(() => setNotification(null), 3000);
          return;
        }

        validFiles.push(file);
        previewUrls.push(URL.createObjectURL(file));
      });

      if (validFiles.length > 0) {
        setMediaFiles((prev) => [...prev, ...validFiles]);
        setMediaPreviewUrls((prev) => [...prev, ...previewUrls]);
      }

      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    };

    const convertFileToBase64 = (file: File): Promise<string> => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const base64 = reader.result as string;
          resolve(base64);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    };

    // Marketplace chat-with-simulation is capped server-side per study (#33/#45).
    // The count is AUTHORITATIVE on the server (enforced on every send); we seed it
    // here so the cap survives reloads / new tabs instead of resetting to zero the
    // way an in-session message count would. Falls back to the message count only
    // until the server value arrives (or if the study id is unknown).
    // Keep the WHOLE server quota, not just the used count: `queries_allowed` is
    // the server's own cap, so the UI can never disagree with what the backend
    // actually enforces. MARKETPLACE_SIM_CHAT_QUERY_CAP is only a fallback for the
    // brief window before this resolves (and if the request fails).
    const [serverSimQuota, setServerSimQuota] = useState<SimChatContext | null>(null);
    useEffect(() => {
      if (!marketplaceView || !marketplaceStudyId) return;
      let active = true;
      getSimChatContext(marketplaceStudyId)
        .then((c) => {
          if (active) setServerSimQuota(c);
        })
        .catch(() => {
          /* keep the message-count + constant fallback */
        });
      return () => {
        active = false;
      };
    }, [marketplaceView, marketplaceStudyId]);

    const messageQueryCount = chatState.messages.filter((m) =>
      ['user', 'use', 'use_persona'].includes(m.role),
    ).length;
    const marketplaceSimQueriesUsed = marketplaceView
      ? (serverSimQuota?.queries_used ?? messageQueryCount)
      : 0;
    const marketplaceSimQueryCap =
      serverSimQuota?.queries_allowed ?? MARKETPLACE_SIM_CHAT_QUERY_CAP;
    const marketplaceSimCapReached =
      marketplaceView && marketplaceSimQueriesUsed >= marketplaceSimQueryCap;

    const handleSendMessage = async () => {
      if (marketplaceSimCapReached) {
        setNotification({
          message: `You've used all ${marketplaceSimQueryCap} chat queries for this study.`,
          type: 'error',
        });
        return;
      }
      if (
        !simulationId ||
        (!chatMessage.trim() && mediaFiles.length === 0) ||
        chatState.isTyping ||
        chatState.isSending ||
        isUploadingMedia
      )
        return;

      const message = chatMessage.trim();
      let optimisticMessageId: string | null = null;

      try {
        setChatMessage('');

        if (mediaFiles.length > 0) {
          setIsUploadingMedia(true);
        }

        let mediaData: any = {};
        if (mediaFiles.length > 0) {
          const images: string[] = [];
          const videos: string[] = [];

          for (const file of mediaFiles) {
            const base64Data = await convertFileToBase64(file);

            if (file.type.startsWith('image/')) {
              images.push(base64Data);
            } else if (file.type.startsWith('video/')) {
              videos.push(base64Data);
            }
          }

          if (images.length > 0) mediaData.images = images;
          if (videos.length > 0) mediaData.videos = videos;
        }

        const tempMediaFiles = [...mediaFiles];
        const tempPreviewUrls = [...mediaPreviewUrls];

        let chatType: 'persona' | 'simulation';
        let options: any = {};

        if (chatTab === 'simulation') {
          chatType = 'simulation';
          options = {
            simulationId: parseInt(String(simulationId)),
            ...mediaData,
          };
        } else if (chatTab === 'persona' && selectedPersona) {
          chatType = 'persona';
          options = {
            simulationId: parseInt(String(simulationId)),
            personaId: selectedPersona,
            ...mediaData,
          };
        } else {
          throw new Error('Invalid chat target');
        }

        const messageMedia =
          tempMediaFiles.length > 0
            ? {
                images: mediaData.images || [],
                videos: mediaData.videos || [],
              }
            : undefined;

        optimisticMessageId = addOptimisticMessage(
          message || 'Please analyze the attached media',
          chatType,
          messageMedia,
        );

        await sendChatMessage(message || 'Please analyze the attached media', chatType, options);

        // Server charged one sim-chat query on accept — mirror it optimistically so
        // the counter and cap update immediately (reseeded from the server on reload).
        // applyOptimisticSimQuery bumps queries_used AND queries_remaining together so
        // the quota stays internally consistent; it leaves a null quota untouched, in
        // which case the counter already advances via messageQueryCount from the
        // optimistic message.
        if (marketplaceView) {
          setServerSimQuota(applyOptimisticSimQuery);
        }

        setMediaFiles([]);
        setMediaPreviewUrls([]);

        tempPreviewUrls.forEach((url) => {
          if (url.startsWith('blob:')) {
            URL.revokeObjectURL(url);
          }
        });
      } catch (err) {
        console.error('Error sending chat message:', err);
        setError(`Failed to send message: ${apiErrorMessage(err, 'Unknown error')}`);

        if (optimisticMessageId) {
          removeOptimisticMessage(optimisticMessageId);
        }

        setChatMessage(message);
        setIsUploadingMedia(false);
      }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSendMessage();
      }
    };

    // Helper function to convert image URL to base64 (converts to JPEG/PNG for PDF compatibility)
    const convertImageToBase64 = async (url: string): Promise<string> => {
      try {
        // Check if this is a question-images URL - use the base64 endpoint to avoid CORS issues
        // Matches: /api/question-images/serve/{user_id}/{filename} or full URL with same path
        const questionImageMatch = url.match(/\/api\/question-images\/serve\/(\d+)\/([^?#]+)/);

        if (questionImageMatch) {
          const userId = questionImageMatch[1];
          const filename = questionImageMatch[2];
          const base64Url = `${API_URL}/api/question-images/base64/${userId}/${filename}`;

          console.log(
            `[PDF Export] Using base64 endpoint for question image: ${url} -> ${base64Url}`,
          );

          const response = await fetch(base64Url, {
            credentials: 'include',
            signal: AbortSignal.timeout(30000),
          });

          if (!response.ok) {
            console.error(
              `[PDF Export] Failed to get base64 image: ${base64Url}, status: ${response.status}`,
            );
            throw new Error(`Failed to get base64 image: ${response.status}`);
          }

          const data = await response.json();
          if (data.data_url) {
            console.log(`[PDF Export] Successfully got base64 for question image: ${filename}`);
            return data.data_url;
          }

          throw new Error('No data_url in response');
        }

        // For other images, use the standard approach
        // Ensure we have an absolute URL
        let absoluteUrl: string;
        if (url.startsWith('http')) {
          absoluteUrl = url;
        } else if (
          url.startsWith('/api') ||
          url.startsWith('/uploads') ||
          url.startsWith('/static')
        ) {
          // Backend API URLs need the API_URL prefix
          absoluteUrl = `${API_URL}${url}`;
        } else if (url.startsWith('/')) {
          // Other absolute paths use origin
          absoluteUrl = `${window.location.origin}${url}`;
        } else {
          // Relative paths use origin
          absoluteUrl = `${window.location.origin}/${url}`;
        }

        console.log(`[PDF Export] Converting image via fetch: ${url} -> ${absoluteUrl}`);

        // Fetch image with credentials
        const response = await fetch(absoluteUrl, {
          credentials: 'include',
          // Add timeout for image fetching
          signal: AbortSignal.timeout(30000),
        });

        if (!response.ok) {
          console.error(
            `[PDF Export] Failed to fetch image: ${absoluteUrl}, status: ${response.status}`,
          );
          throw new Error(`Failed to fetch image: ${response.status}`);
        }

        const blob = await response.blob();
        console.log(`[PDF Export] Fetched image blob: ${blob.type}, size: ${blob.size}`);

        // Convert to JPEG/PNG for wkhtmltopdf compatibility (doesn't support WebP well)
        return new Promise((resolve, reject) => {
          const img = new Image();
          img.crossOrigin = 'anonymous';

          img.onload = () => {
            try {
              // Create canvas to convert image
              const canvas = document.createElement('canvas');
              canvas.width = img.width;
              canvas.height = img.height;

              const ctx = canvas.getContext('2d');
              if (!ctx) {
                throw new Error('Failed to get canvas context');
              }

              // Draw image on canvas
              ctx.drawImage(img, 0, 0);

              // Convert to JPEG (or PNG for images with transparency)
              // Use JPEG for better compression for photos, PNG for graphics/transparency
              const outputFormat = 'image/jpeg';
              const quality = 0.85; // Good quality with reasonable file size

              // Get base64 string
              const base64 = canvas.toDataURL(outputFormat, quality);
              console.log(`[PDF Export] Successfully converted image to base64: ${url}`);
              resolve(base64);
            } catch (err) {
              console.error('[PDF Export] Canvas conversion error:', err);
              reject(err);
            }
          };

          img.onerror = (e) => {
            console.error(`[PDF Export] Image element failed to load: ${url}`, e);
            reject(new Error('Failed to load image in Image element'));
          };

          // Convert blob to data URL for the image element
          const reader = new FileReader();
          reader.onloadend = () => {
            img.src = reader.result as string;
          };
          reader.onerror = (e) => {
            console.error('[PDF Export] FileReader error:', e);
            reject(e);
          };
          reader.readAsDataURL(blob);
        });
      } catch (error) {
        console.error('[PDF Export] Failed to convert image:', url, error);
        // Return a transparent 1x1 pixel as fallback
        return 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNkYPhfDwAChwGA60e6kgAAAABJRU5ErkJggg==';
      }
    };

    // Enhanced content preparation function for export
    const prepareContentForExport = async () => {
      try {
        // 1. Set export mode for specialized components
        setIsPreparingExport(true);

        // 2. Wait for export state to propagate and images to re-render
        await new Promise((resolve) => setTimeout(resolve, 300));

        // 3. Get the analysis section
        const analysisSection = document.getElementById('simulation-analysis-section');
        if (!analysisSection) {
          throw new Error('Analysis section not found');
        }

        // 4. Clone the section to avoid modifying the actual DOM
        const clonedSection = analysisSection.cloneNode(true) as HTMLElement;

        // 5. Convert all images to base64 (including images inside foreignObject elements)
        const images = clonedSection.querySelectorAll('img');
        console.log(`[PDF Export] Found ${images.length} images to convert to base64`);

        let successCount = 0;
        let failCount = 0;

        const imageConversions = Array.from(images).map(async (img) => {
          const src = img.getAttribute('src');
          if (src && !src.startsWith('data:')) {
            try {
              const base64 = await convertImageToBase64(src);
              img.setAttribute('src', base64);
              // Also remove loading="lazy" attribute that might cause issues
              img.removeAttribute('loading');
              successCount++;
            } catch (error) {
              console.error('[PDF Export] Failed to convert image:', src, error);
              // Set a placeholder image instead of leaving broken src
              img.setAttribute(
                'src',
                'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNkYPhfDwAChwGA60e6kgAAAABJRU5ErkJggg==',
              );
              failCount++;
            }
          }
        });

        // Wait for all image conversions
        await Promise.all(imageConversions);
        console.log(
          `[PDF Export] Image conversion complete: ${successCount} success, ${failCount} failed`,
        );

        // 6. Also handle background images in style attributes
        const elementsWithBackgroundImage = clonedSection.querySelectorAll(
          '[style*="background-image"]',
        );
        for (const el of elementsWithBackgroundImage) {
          const style = el.getAttribute('style') || '';
          const urlMatch = style.match(/url\(['"]?([^'")\s]+)['"]?\)/);
          if (urlMatch && urlMatch[1] && !urlMatch[1].startsWith('data:')) {
            try {
              const base64 = await convertImageToBase64(urlMatch[1]);
              const newStyle = style.replace(/url\(['"]?[^'")\s]+['"]?\)/, `url('${base64}')`);
              el.setAttribute('style', newStyle);
            } catch (error) {
              console.error('[PDF Export] Failed to convert background image:', urlMatch[1], error);
            }
          }
        }

        // 7. Remove no-print elements and buttons
        const noPrintElements = clonedSection.querySelectorAll('.no-print, button');
        noPrintElements.forEach((el) => el.remove());

        // 8. Get the HTML content with embedded images
        const divHtml = clonedSection.outerHTML;

        return divHtml;
      } finally {
        // Reset export mode
        setIsPreparingExport(false);
      }
    };

    // PDF generation functions
    const startAsyncPdfGeneration = async (shouldSendEmail = false, emailRecipient = '') => {
      try {
        setPdfGenerating(true);
        setCachedPdfUrl(null);
        setPdfCacheKey(null);

        if (pdfCheckInterval) {
          clearInterval(pdfCheckInterval);
          setPdfCheckInterval(null);
        }

        // These task types render server-side (WeasyPrint) from stored data — no DOM scrape.
        // The response shape ({success, cache_key} then {ready, url}) is identical to the legacy
        // path, so the polling/download tail below is shared unchanged. Other task types keep the
        // legacy DOM-scrape path until the branded renderer covers them (bluepillai/backend#419).
        // The backend branded renderer now covers EVERY simulation: bespoke templates for
        // packaging-variant-comparison, survey_and_focus_groups, vitamin-concept-test,
        // claims_testing, channel-event-strategy (A/B creatives) and packaging-review-v2, plus a
        // generic on-brand fallback for anything else. So every sim export goes server-side and
        // none hits the broken DOM-scrape → wkhtmltopdf path. The legacy `else` only runs if there
        // is no simulationId to render from.
        let response: Response;
        if (simulationId) {
          response = await fetch(`${API_URL}/api/request-branded-pdf/${simulationId}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
          });
        } else {
          // Legacy path: scrape the rendered analysis DOM and send it to the HTML→PDF service.
          const divHtml = await prepareContentForExport();

          const styles = Array.from(document.querySelectorAll('link[rel="stylesheet"], style'))
            .map((node) => {
              if (node instanceof HTMLLinkElement) {
                const absoluteUrl = new URL(node.href, window.location.origin).href;
                return `<link rel="stylesheet" href="${absoluteUrl}">`;
              }
              return node.outerHTML;
            })
            .join('\n');

          const customPrintStyle = `
        <style>
          .no-print { display: none !important; }
          button { display: none !important; }
          @media print {
            @page { margin: 0.75in; size: letter; }
            * { -webkit-print-color-adjust: exact !important; }
            body { font-family: 'Inter', sans-serif; line-height: 1.4; }
          }
          table { border-collapse: collapse; width: 100%; }
          th, td { padding: 12px; text-align: left; }
          .text-primary { color: #07E5D1; }
          .bg-primary-light { background-color: #E8FCFA; }
        </style>
        `;

          const reportHtml = `
        <!DOCTYPE html>
        <html lang="en">
        <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>Simulation Analysis Report</title>
          ${styles}
          ${customPrintStyle}
        </head>
        <body style="background-color: white; padding: 20px;">
          ${divHtml}
        </body>
        </html>
        `;

          response = await fetch(`${API_URL}/api/request-pdf`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              html: reportHtml,
              simulationId: simulationId || 'simulation',
            }),
            credentials: 'include',
          });
        }

        if (!response.ok) throw new Error('Failed to initiate PDF generation');

        const data = await response.json();
        setPdfCacheKey(data.cache_key);

        if (data.message === 'PDF already available') {
          setPdfGenerating(false);
          const pdfUrl = `${API_URL}/api/get-pdf/${data.cache_key}`;
          setCachedPdfUrl(pdfUrl);

          if (shouldSendEmail) {
            await sendPdfByEmailInternal(data.cache_key, emailRecipient);
          } else {
            downloadPdf(pdfUrl);
          }
          return;
        }

        // Start polling for PDF status
        const interval = setInterval(async () => {
          try {
            const statusResponse = await fetch(
              `${API_URL}/api/check-pdf-status/${simulationId || 'simulation'}`,
              {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ cache_key: data.cache_key }),
                credentials: 'include',
              },
            );

            if (statusResponse.ok) {
              const statusData = await statusResponse.json();
              if (statusData.ready) {
                clearInterval(interval);
                setPdfCheckInterval(null);
                setPdfGenerating(false);
                const pdfUrl = `${API_URL}${statusData.url}`;
                setCachedPdfUrl(pdfUrl);

                if (shouldSendEmail) {
                  await sendPdfByEmailInternal(data.cache_key, emailRecipient);
                } else {
                  downloadPdf(pdfUrl);
                }
              }
            }
          } catch (error) {
            console.error('Error checking PDF status:', error);
          }
        }, 2000);

        setPdfCheckInterval(interval);

        setTimeout(() => {
          if (interval) {
            clearInterval(interval);
            setPdfCheckInterval(null);
            setPdfGenerating(false);
            setExportError('PDF generation timed out. Please try again.');
          }
        }, 300000);
      } catch (error) {
        console.error('Error starting PDF generation:', error);
        setPdfGenerating(false);
        setExportError('Failed to generate PDF. Please try again.');
      }
    };

    const downloadPdf = (pdfUrl: string) => {
      const link = document.createElement('a');
      link.href = pdfUrl;
      const timestamp = new Date().toISOString().split('T')[0];
      const simId = simulation?.id || 'unknown';
      link.download = `simulation-${simId}-${timestamp}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    };

    const sendPdfByEmailInternal = async (cacheKey: string, emailRecipient: string) => {
      try {
        const contentData = parseContentField();
        const simulationName = contentData.name || `Simulation ${simulationId}`;

        const res = await fetch(`${API_URL}/send-pdf-by-cache-key`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            cache_key: cacheKey,
            to: emailRecipient,
            subject: `${simulationName} - Analysis PDF`,
            simulation_name: simulationName,
          }),
          credentials: 'include',
          signal: AbortSignal.timeout(60000),
        });

        if (!res.ok) {
          const errorData = await res.json().catch(() => ({}));
          throw new Error(errorData.detail || 'Failed to send email');
        }
        setExportEmailStatus('success');
      } catch (err: any) {
        setExportEmailStatus('error');
        setExportEmailError(err.message || 'Failed to send email');
      }
    };

    const sendPdfByEmail = async (email: string) => {
      if (!email) return;

      setCurrentEmailRecipient(email);
      setExportEmailStatus('sending');
      setExportEmailError('');

      await startAsyncPdfGeneration(true, email);
    };

    const handleEmailNotification = async (email: string) => {
      if (!simulationId) return;
      setEmailNotificationStatus('sending');
      setEmailNotificationError('');
      try {
        const response = await fetch(`${API_URL}/monitor-simulation`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({
            simulationId: simulationId,
            email: email,
          }),
        });
        if (!response.ok) {
          throw new Error('Failed to set up email notification');
        }
        setEmailNotificationStatus('success');
        setShowEmailModal(false);
        setNotificationEmail(email);
      } catch (err) {
        console.error('Error setting up email notification:', err);
        setEmailNotificationStatus('error');
        setEmailNotificationError('Failed to set up email notification. Please try again.');
      }
    };

    const renderLoadingAnimation = () => {
      const isSimulationRunning = !simulation?.simulation_response;
      const isRendering = !!simulation?.simulation_response && loading;

      return (
        <div className="flex flex-col items-center justify-center min-h-[70vh]">
          <div className="animate-spin mb-6">
            <Loader2 className="h-12 w-12 text-primary" />
          </div>
          <h3 className="text-xl font-semibold mb-2">
            {isSimulationRunning
              ? simulationStatus === 'pending'
                ? 'Starting simulation...'
                : currentStep
              : isRendering
                ? 'Rendering simulation...'
                : currentStep}
          </h3>
          <p className="text-gray-500 text-center max-w-md mb-4">
            {isSimulationRunning
              ? 'Please wait while we run your simulation.'
              : isRendering
                ? 'Rendering your simulation results. This may take a few moments.'
                : ''}
          </p>
          {/* Estimated Time Display */}
          {isSimulationRunning && estimatedTime && (
            <div className="text-sm text-gray-600 mb-6 text-center">
              {remainingMinutes !== null && remainingMinutes > 1 ? (
                <>
                  <div className="font-medium text-gray-800">
                    Estimated time: ~
                    {remainingMinutes !== null ? remainingMinutes : estimatedTime.totalMinutes} min
                  </div>
                  {estimatedTime.queueMinutes > 0 && (
                    <div className="text-xs text-gray-500 mt-1">
                      ({estimatedTime.baseMinutes} min processing + {estimatedTime.queueMinutes} min
                      queue wait)
                    </div>
                  )}
                  {estimatedTime.queuedCount > 0 && (
                    <div className="text-xs text-gray-400 mt-1">
                      {estimatedTime.queuedCount} simulation
                      {estimatedTime.queuedCount > 1 ? 's' : ''} ahead in queue
                    </div>
                  )}
                </>
              ) : remainingMinutes === 1 ? (
                <div className="font-medium text-gray-800">Almost done...</div>
              ) : (
                <div className="font-medium text-gray-800">Finishing up — just a moment longer</div>
              )}
            </div>
          )}
          {isSimulationRunning && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 max-w-md w-full">
              <div className="flex items-start space-x-3">
                <Mail className="h-5 w-5 text-gray-600 mt-0.5 flex-shrink-0" />
                {/* In the marketplace the study already emails the signed-in account, so this
                  card REASSURES ("we'll email <you>") and only offers to add another address
                  — it doesn't ask for an email the system already has. */}
                {marketplaceView && notifyDefaultEmail ? (
                  <div className="flex-1">
                    <h4 className="text-sm font-medium text-gray-900 mb-1">
                      We'll email you when it's ready
                    </h4>
                    <p className="text-sm text-gray-600 mb-2">
                      Your results will be sent to{' '}
                      <span className="font-medium text-gray-900">{notifyDefaultEmail}</span> — you
                      can safely close this page.
                    </p>
                    {emailNotificationStatus === 'success' &&
                    notificationEmail &&
                    notificationEmail !== notifyDefaultEmail ? (
                      <div className="text-sm text-primary font-medium">
                        ✓ We'll also notify {notificationEmail}
                      </div>
                    ) : (
                      <button
                        className="text-sm text-primary hover:underline font-medium disabled:opacity-60"
                        onClick={() => {
                          setNotificationEmail('');
                          setShowEmailModal(true);
                        }}
                        disabled={emailNotificationStatus === 'sending'}
                      >
                        {emailNotificationStatus === 'sending'
                          ? 'Setting up…'
                          : 'Send to a different email'}
                      </button>
                    )}
                    {emailNotificationStatus === 'error' && (
                      <div className="mt-2 text-sm text-red-500">{emailNotificationError}</div>
                    )}
                  </div>
                ) : (
                  <div className="flex-1">
                    <h4 className="text-sm font-medium text-gray-900 mb-1">
                      Get notified when ready
                    </h4>
                    <p className="text-sm text-gray-600 mb-3">
                      We'll email you once your simulation is complete so you can step away.
                    </p>
                    <button
                      className="w-full bg-primary hover:bg-primary-hover text-white font-semibold py-2 px-4 rounded transition-colors disabled:opacity-60"
                      onClick={() => setShowEmailModal(true)}
                      disabled={emailNotificationStatus === 'sending'}
                    >
                      {emailNotificationStatus === 'sending'
                        ? 'Setting up...'
                        : 'Get Email Notification'}
                    </button>
                    {emailNotificationStatus === 'success' && (
                      <div className="mt-2 text-sm text-primary font-medium">
                        ✓ Email notification set up successfully!
                      </div>
                    )}
                    {emailNotificationStatus === 'error' && (
                      <div className="mt-2 text-sm text-red-500">{emailNotificationError}</div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      );
    };

    // Memoized so the (potentially large) content JSON is parsed only when it actually changes,
    // not on every re-render. Without this, transient re-renders during load (socket connect,
    // status transitions) each re-parse the content and re-derive the report, which for a large
    // simulation is seconds of synchronous work per render and locks the tab.
    const contentData = useMemo(() => parseContentField(), [simulation?.content]);
    const failure = useMemo(
      () => (simulation ? simulationDetailFailure(simulation) : null),
      [simulation],
    );
    const canActOnFailure = !isPublicView && !isAdminView && !isRatingView && !marketplaceView;

    // Handle error state for public/admin view - return early
    if (error && (isPublicView || isAdminView || isRatingView)) {
      return (
        <div className="flex items-center justify-center py-16">
          <div className="text-center max-w-md">
            <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <X className="w-8 h-8 text-red-500" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              {isAdminView ? 'Simulation Not Found' : 'Simulation Not Available'}
            </h3>
            <p className="text-gray-600">{error}</p>
          </div>
        </div>
      );
    }

    return (
      <div
        className={`simulation-results-container ${isPrintMode ? 'print-mode h-auto overflow-visible' : 'h-full'}`}
      >
        {error && !isPrintMode && (
          <div className="bg-red-50 border border-red-200 text-red-500 px-4 py-3 rounded mb-6">
            {error}
          </div>
        )}

        {simulationStatus === 'failed' && failure ? (
          <SimulationFailedPanel
            failure={failure}
            simulationId={simulation?.id ?? simulationId}
            simulationName={simulation?.name || contentData?.name}
            fallbackTime={simulation?.updated_at}
            onRunAgain={canActOnFailure ? () => setShowEditRerunModal(true) : undefined}
            onBackToHistory={canActOnFailure ? () => navigate(HISTORY_PATH) : undefined}
          />
        ) : loading && !simulation ? (
          <div className="flex flex-col items-center justify-center min-h-[70vh]">
            <div className="animate-spin mb-6">
              <Loader2 className="h-12 w-12 text-primary" />
            </div>
            <h3 className="text-xl font-semibold mb-2">Loading simulation...</h3>
            <p className="text-gray-500 text-center max-w-md">Please wait</p>
          </div>
        ) : loading && simulation && !simulation.simulation_response ? (
          renderLoadingAnimation()
        ) : loading && simulation && simulation.simulation_response ? (
          <div className="flex flex-col items-center justify-center min-h-[70vh]">
            <div className="animate-spin mb-6">
              <Loader2 className="h-12 w-12 text-primary" />
            </div>
            <h3 className="text-xl font-semibold mb-2">Loading simulation...</h3>
            <p className="text-gray-500 text-center max-w-md">Please wait</p>
          </div>
        ) : (
          <div
            className={`flex ${isPrintMode || hideHeader || isRatingView ? 'h-auto' : 'h-full'} ${
              marketplaceView && !isPrintMode && !hideHeader
                ? 'h-auto flex-col gap-4 md:h-[80vh] md:flex-row md:gap-0'
                : ''
            }`}
            // The 80vh row height only makes sense once the panes are side by side; on a
            // stacked phone layout each pane sizes itself (see below). Rating view is excluded
            // for a different reason: inputs sit above the results there, and a fixed 80vh would
            // squeeze both into a keyhole instead of letting the page scroll.
            style={
              isPrintMode || hideHeader || marketplaceView || isRatingView ? {} : { height: '80vh' }
            }
          >
            {/* Chat Interface — private + marketplace (marketplace is capped, see below) */}
            {!hideChat && !isPublicView && !isAdminView && !isRatingView && !isPrintMode && (
              <div
                className={`bg-white rounded-xl shadow-sm relative transition-all duration-300 ease-in-out ${
                  isChatCollapsed
                    ? // Collapsing to w-0 only removes the pane from a ROW. Stacked on a
                      // phone the width is the cross axis, so a w-0 pane still reserves its
                      // height and pushes the report down behind an invisible block — which
                      // is precisely the state a phone starts in. Zero the height too, and
                      // restore it at md where the row layout takes over.
                      'h-0 w-0 opacity-0 md:h-full'
                    : marketplaceView
                      ? `${MOBILE_CHAT_HEIGHT} w-full opacity-100 md:h-full md:w-1/3`
                      : 'h-full w-1/3 opacity-100'
                }`}
                style={{ overflow: isChatCollapsed ? 'hidden' : 'visible' }}
              >
                <div
                  className={`h-full transition-opacity duration-300 ease-in-out ${isChatCollapsed ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}
                >
                  <ChatInterface
                    chatTab={chatTab}
                    chatState={chatState}
                    chatMessage={chatMessage}
                    mediaFiles={mediaFiles}
                    mediaPreviewUrls={mediaPreviewUrls}
                    isChatConnected={isChatConnected}
                    isUploadingMedia={isUploadingMedia}
                    isPreparingExport={isPreparingExport}
                    onChatTabChange={handleChatTabChange}
                    onChatMessageChange={setChatMessage}
                    onSendMessage={handleSendMessage}
                    onKeyDown={handleKeyDown}
                    onFileSelect={handleFileSelect}
                    onRemoveMedia={handleRemoveMedia}
                    onUploadClick={handleUploadClick}
                    onViewMedia={handleViewMedia}
                    fileInputRef={fileInputRef}
                    queryCap={marketplaceView ? marketplaceSimQueryCap : undefined}
                    queriesUsed={marketplaceView ? marketplaceSimQueriesUsed : undefined}
                  />
                </div>
              </div>
            )}

            {/* Main Content Area */}
            <div
              className={`bg-white rounded-xl shadow-sm flex flex-col transition-all duration-300 ease-in-out ${isPrintMode || hideHeader ? 'overflow-visible h-auto w-full' : isPublicView ? 'overflow-visible h-auto md:overflow-hidden md:h-full' : marketplaceView ? `${MOBILE_REPORT_FLOW} md:h-full md:overflow-hidden` : 'overflow-hidden h-full'} ${
                !isPrintMode &&
                !hideHeader &&
                (isPublicView || isAdminView || hideChat
                  ? 'w-full'
                  : marketplaceView
                    ? isChatCollapsed
                      ? 'w-full md:w-[calc(100%-3rem)]'
                      : 'w-full md:w-2/3'
                    : isChatCollapsed
                      ? 'w-[calc(100%-3rem)]'
                      : 'w-2/3')
              } ${hideHeader ? 'w-full' : ''}`}
            >
              {/* Header - hidden during print or when hideHeader */}
              <div
                className={`border-b border-gray-200 bg-white px-3 md:px-6 py-2 md:py-3 ${isPrintMode || hideHeader ? 'hidden' : ''}`}
              >
                <div className="flex justify-between items-center">
                  <div className="flex items-center">
                    {/* Chat toggle: available in the private view AND the marketplace,
                      so a study's results can be read full-width with the chat
                      panel collapsed (same affordance as the main platform).
                      Still hidden for public/admin views, which have no chat. */}
                    {!hideChat &&
                      !isPublicView &&
                      !isAdminView &&
                      !isRatingView &&
                      (marketplaceView && isChatCollapsed ? (
                        // The marketplace opens on the report with the chat collapsed, so this
                        // is the discoverable way in. Outline chrome matches the "Simulation
                        // Inputs" control next to it; the label + icon carry the animated
                        // gradient (same affordance as the vitamin "See sample" button) so it
                        // still reads as the special, inviting action without a loud fill.
                        <button
                          onClick={handleToggleChat}
                          className="flex items-center space-x-2 px-3 py-2 mr-8 rounded-md border border-primary transition-colors hover:bg-primary-light focus:outline-none focus:ring-2 focus:ring-primary min-h-[44px] md:h-9 md:min-h-0"
                        >
                          {/* hidden gradient def so the lucide icon strokes with the same ramp */}
                          <svg width="0" height="0" className="absolute" aria-hidden="true">
                            <defs>
                              <linearGradient id="mktChatCtaGrad" x1="0" y1="0" x2="1" y2="0">
                                <stop offset="0%" stopColor="#07E5D1" />
                                <stop offset="40%" stopColor="#6366f1" />
                                <stop offset="70%" stopColor="#ec4899" />
                                <stop offset="100%" stopColor="#f59e0b" />
                              </linearGradient>
                            </defs>
                          </svg>
                          <MessageSquare
                            className="h-4 w-4"
                            style={{ stroke: 'url(#mktChatCtaGrad)' }}
                          />
                          <span className="text-sm font-semibold mkt-chat-cta-text">
                            Chat with these results
                          </span>
                        </button>
                      ) : (
                        // State-aware toggle: when the chat is OPEN this is a clear collapse
                        // control (panel-close arrow + "Hide chat"), so it never just reads
                        // "Chat" while the panel is already open. Collapsed (enterprise) shows
                        // the open affordance.
                        <button
                          onClick={handleToggleChat}
                          className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-md transition-colors mr-8"
                          title={isChatCollapsed ? 'Open chat' : 'Hide chat'}
                        >
                          {isChatCollapsed ? (
                            <>
                              <MessageSquare className="h-4 w-4" />
                              <span>Chat</span>
                            </>
                          ) : (
                            <>
                              <PanelLeftClose className="h-4 w-4" />
                              <span>Hide chat</span>
                            </>
                          )}
                        </button>
                      ))}
                    {!marketplaceView && (
                      <nav className="flex gap-4" aria-label="Tabs">
                        <button
                          onClick={() => handleActiveTabChange('simulation')}
                          className={`py-2 inline-flex items-center border-b-2 text-sm font-medium ${
                            activeTab === 'simulation'
                              ? 'border-primary text-gray-900'
                              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                          }`}
                        >
                          <BarChart2 className="mr-2 h-4 w-4" />
                          Simulation Analysis
                        </button>
                      </nav>
                    )}
                  </div>
                  {!isPublicView && !isAdminView && !isRatingView && (
                    <div className="flex items-center space-x-2">
                      {/* Share/Unshare Button */}
                      {!isPublicShared ? (
                        <div className="relative">
                          <button
                            onClick={handleShare}
                            disabled={isSharing}
                            onMouseEnter={() => setShowTooltip('share')}
                            onMouseLeave={() => setShowTooltip('')}
                            className="p-2 rounded-md border border-primary transition-colors disabled:opacity-50 text-primary hover:bg-primary-light h-9 w-9"
                          >
                            {isSharing ? (
                              <RefreshCw className="w-5 h-5 animate-spin" />
                            ) : (
                              <Share2 className="w-5 h-5" />
                            )}
                          </button>
                          {showTooltip === 'share' && (
                            <div className="absolute left-1/2 -translate-x-1/2 mt-2 bg-gray-800 text-white text-xs px-2 py-1 rounded whitespace-nowrap z-20">
                              Share simulation
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="relative" ref={shareDropdownRef}>
                          <button
                            onClick={() => setShareDropdownOpen(!shareDropdownOpen)}
                            onMouseEnter={() => setShowTooltip('shareManage')}
                            onMouseLeave={() => setShowTooltip('')}
                            className="flex items-center gap-1 p-2 rounded-md bg-gray-50 border border-primary hover:bg-gray-100 transition-colors text-primary h-9"
                          >
                            <Share2 className="w-5 h-5" />
                            <ChevronDown className="w-3 h-3" />
                          </button>
                          {showTooltip === 'shareManage' && !shareDropdownOpen && (
                            <div className="absolute left-1/2 -translate-x-1/2 mt-2 bg-gray-800 text-white text-xs px-2 py-1 rounded whitespace-nowrap z-20">
                              Manage sharing
                            </div>
                          )}

                          {shareDropdownOpen && (
                            <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                              <button
                                onClick={() => {
                                  setShareDropdownOpen(false);
                                  setIsShareModalOpen(true);
                                }}
                                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                              >
                                <ExternalLink className="w-4 h-4" />
                                View Share Link
                              </button>
                              <button
                                onClick={() => {
                                  setShareDropdownOpen(false);
                                  handleUnshare();
                                }}
                                disabled={isUnsharing}
                                className="w-full px-4 py-2 text-left text-sm text-red-500 hover:bg-red-50 flex items-center gap-2 disabled:opacity-50"
                              >
                                <ShieldOff className="w-4 h-4" />
                                {isUnsharing ? 'Making Private...' : 'Make Private'}
                              </button>
                            </div>
                          )}
                        </div>
                      )}

                      {!marketplaceView && (
                        <>
                          {/* Copy Button */}
                          <div className="relative">
                            <button
                              onMouseEnter={() => setShowTooltip('copy')}
                              onMouseLeave={() => setShowTooltip('')}
                              onClick={handleCopyToClipboard}
                              className={`p-2 rounded-md border transition-colors focus:outline-none focus:ring-2 h-9 w-9 ${
                                copied
                                  ? 'text-primary bg-primary-light border-primary/20 focus:ring-primary'
                                  : 'text-primary border-primary hover:bg-primary-light focus:ring-primary'
                              }`}
                              title="Copy simulation output"
                              aria-label="Copy simulation output"
                            >
                              <Copy className="h-5 w-5" />
                            </button>
                            {showTooltip === 'copy' && (
                              <div className="absolute left-1/2 -translate-x-1/2 mt-2 bg-gray-800 text-white text-xs px-2 py-1 rounded whitespace-nowrap z-20">
                                Click to copy output
                              </div>
                            )}
                          </div>
                          {copied && (
                            <span className="text-primary text-xs ml-1 transition-opacity duration-200">
                              Copied!
                            </span>
                          )}

                          {/* Print vs Export. vitamin-concept-test now uses Export → the server-side branded
                      PDF (better than the browser print dialog); packaging-review-v2 stays on Print
                      until the branded renderer covers it (bluepillai/backend#419). */}
                          {contentData?.task === 'packaging-review-v2' ? (
                            <div className="relative">
                              <button
                                onMouseEnter={() => setShowTooltip('print')}
                                onMouseLeave={() => setShowTooltip('')}
                                onClick={handlePrintToPdf}
                                disabled={isPrintPreparing}
                                className="p-2 rounded-md border border-primary transition-colors focus:outline-none focus:ring-2 focus:ring-primary text-primary hover:bg-primary-light h-9 w-9 disabled:opacity-50"
                                title="Print to PDF"
                                aria-label="Print to PDF"
                              >
                                {isPrintPreparing ? (
                                  <Loader2 className="h-5 w-5 animate-spin" />
                                ) : (
                                  <Printer className="h-5 w-5" />
                                )}
                              </button>
                              {showTooltip === 'print' && (
                                <div className="absolute left-1/2 -translate-x-1/2 mt-2 bg-gray-800 text-white text-xs px-2 py-1 rounded whitespace-nowrap z-20">
                                  Print to PDF
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="relative">
                              <button
                                onMouseEnter={() => setShowTooltip('export')}
                                onMouseLeave={() => setShowTooltip('')}
                                onClick={handleOpenExportModal}
                                className="p-2 rounded-md border border-primary transition-colors focus:outline-none focus:ring-2 focus:ring-primary text-primary hover:bg-primary-light h-9 w-9"
                                title="Export simulation analysis"
                                aria-label="Export simulation analysis"
                              >
                                <Download className="h-5 w-5" />
                              </button>
                              {showTooltip === 'export' && (
                                <div className="absolute left-1/2 -translate-x-1/2 mt-2 bg-gray-800 text-white text-xs px-2 py-1 rounded whitespace-nowrap z-20">
                                  Click to export as PDF
                                </div>
                              )}
                            </div>
                          )}

                          {/* Edit & Rerun Button */}
                          <div className="relative">
                            <button
                              onMouseEnter={() => setShowTooltip('editRerun')}
                              onMouseLeave={() => setShowTooltip('')}
                              onClick={() => setShowEditRerunModal(true)}
                              className="p-2 rounded-md border border-primary transition-colors focus:outline-none focus:ring-2 focus:ring-primary text-primary hover:bg-primary-light h-9 w-9"
                              title="Edit and rerun simulation"
                              aria-label="Edit and rerun simulation"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                            {showTooltip === 'editRerun' && (
                              <div className="absolute left-1/2 -translate-x-1/2 mt-2 bg-gray-800 text-white text-xs px-2 py-1 rounded whitespace-nowrap z-20">
                                Edit inputs and rerun
                              </div>
                            )}
                          </div>

                          {/* Rerun Button */}
                          <div className="relative">
                            <button
                              onMouseEnter={() => setShowTooltip('rerun')}
                              onMouseLeave={() => setShowTooltip('')}
                              onClick={handleRerunSimulation}
                              className="p-2 rounded-md border border-primary transition-colors focus:outline-none focus:ring-2 focus:ring-primary text-primary hover:bg-primary-light disabled:opacity-50 h-9 w-9"
                              disabled={rerunLoading}
                            >
                              <RefreshCw className="h-4 w-4" />
                            </button>
                            {showTooltip === 'rerun' && (
                              <div className="absolute left-1/2 -translate-x-1/2 mt-2 bg-gray-800 text-white text-xs px-2 py-1 rounded whitespace-nowrap z-20">
                                Click to rerun simulation
                              </div>
                            )}
                          </div>
                        </>
                      )}

                      {/* Simulation Inputs Button */}
                      <div className="relative">
                        <button
                          ref={dropdownButtonRef}
                          onMouseEnter={() => setShowTooltip('inputs')}
                          onMouseLeave={() => setShowTooltip('')}
                          onClick={handleToggleDetailsDropdown}
                          className="flex items-center space-x-2 px-3 py-2 rounded-md border border-primary transition-colors text-primary hover:bg-primary-light min-h-[44px] md:h-9 md:min-h-0"
                        >
                          <FileText className="h-5 w-5" />
                          <span className="text-sm">Simulation Inputs</span>
                          {isDetailsDropdownOpen ? (
                            <ChevronUp className="h-5 w-5" />
                          ) : (
                            <ChevronDown className="h-5 w-5" />
                          )}
                        </button>
                        {showTooltip === 'inputs' && (
                          <div className="absolute left-1/2 -translate-x-1/2 mt-2 bg-gray-800 text-white text-xs px-2 py-1 rounded whitespace-nowrap z-20">
                            Click to view simulation inputs
                          </div>
                        )}
                      </div>
                      {rerunError && (
                        <span className="text-red-500 text-xs ml-2">{rerunError}</span>
                      )}
                    </div>
                  )}

                  {/* Read-only banner for public and admin views */}
                  {isPublicView && (
                    <div className="hidden md:block mb-4 px-3 py-2 bg-gray-50 border-l-4 border-gray-400 rounded">
                      <p className="text-xs text-gray-600">Shared view - editing disabled</p>
                    </div>
                  )}
                  {isAdminView && (
                    <div className="mb-4 px-3 py-2 bg-blue-50 border-l-4 border-blue-400 rounded">
                      <p className="text-xs text-blue-600">Admin view - read only</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Main Analysis Panel */}
              <div
                className={`flex-grow relative ${isPrintMode || hideHeader ? 'overflow-visible h-auto' : isPublicView ? 'overflow-visible md:overflow-hidden' : 'overflow-hidden'}`}
              >
                {simulation?.simulation_response && (
                  <SimulationAnalysisPanel
                    simulation={simulation}
                    simulationResponse={simulation.simulation_response}
                    contentData={contentData}
                    isPreparingExport={isPreparingExport}
                    expandedCards={expandedCards}
                    onToggleCard={toggleCard}
                    isDetailsDropdownOpen={isDetailsDropdownOpen}
                    onCloseDropdown={() => setIsDetailsDropdownOpen(false)}
                    dropdownRef={dropdownRef}
                    onImageClick={handleImageClick}
                    inputsInline={inputsInline}
                    inputsOpen={isInlineInputsOpen}
                    onToggleInputs={toggleInlineInputs}
                    inputsSideBySide={isRatingView && !stackInputs}
                    userInputsOnly={isRatingView}
                    isPrintMode={isPrintMode}
                    isPublicView={isPublicView}
                    marketplaceView={marketplaceView}
                    // Rating view reads as one document: with inputs stacked above the results,
                    // the panel's own md:max-h-[calc(90vh-120px)] scroller would trap both in a
                    // keyhole. isEmbedded is the existing escape hatch for exactly that.
                    isEmbedded={hideHeader || isRatingView}
                    simulationId={simulationId}
                  />
                )}
              </div>
            </div>
          </div>
        )}

        {/* Export Modal */}
        <ExportModal
          isOpen={showExportModal}
          onClose={handleCloseExportModal}
          pdfGenerating={pdfGenerating}
          onExportPDF={() => startAsyncPdfGeneration(false)}
          onSendEmail={sendPdfByEmail}
          exportEmailStatus={exportEmailStatus}
          exportEmailError={exportEmailError}
          exportError={exportError}
        />

        {/* Edit & Rerun Modal — only mount when open. The modal renders nothing while closed, but
          mounting it eagerly ran its transformContentDataToFormData (a heavy transform over the
          full content) on load, which for a large simulation is many seconds of synchronous work
          for a modal the user hasn't opened. Gating the mount defers that work to when it's used. */}
        {simulation && showEditRerunModal && (
          <EditRerunModal
            isOpen={showEditRerunModal}
            onClose={() => setShowEditRerunModal(false)}
            simulation={simulation}
            contentData={contentData}
          />
        )}

        {/* Share Modal */}
        {shareUrl && (
          <ShareModal
            isOpen={isShareModalOpen}
            onClose={() => setIsShareModalOpen(false)}
            shareUrl={shareUrl}
            onUnshare={handleUnshare}
            isUnsharing={isUnsharing}
          />
        )}

        {/* Email Notification Modal */}
        {showEmailModal && (
          <Dialog open={showEmailModal} onOpenChange={setShowEmailModal}>
            <DialogContent className="sm:max-w-md">
              <CustomDialogClose onClick={() => setShowEmailModal(false)}>
                <X className="h-5 w-5" />
              </CustomDialogClose>
              <DialogHeader>
                <DialogTitle className="flex items-center">
                  <Mail className="h-5 w-5 mr-2 text-gray-600" />
                  Email Notification
                </DialogTitle>
                <DialogDescription>
                  {marketplaceView && notifyDefaultEmail ? (
                    <>
                      We already email{' '}
                      <span className="font-medium text-gray-900">{notifyDefaultEmail}</span>. Add
                      another address to be notified there too when your study is complete.
                    </>
                  ) : (
                    'Enter your email address to receive a notification when your simulation is complete.'
                  )}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <label
                    htmlFor="notification-email"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    {marketplaceView && notifyDefaultEmail
                      ? 'Additional email address'
                      : 'Email Address'}
                  </label>
                  <input
                    id="notification-email"
                    type="email"
                    value={notificationEmail}
                    onChange={(e) => setNotificationEmail(e.target.value)}
                    placeholder="your.email@example.com"
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                  />
                </div>
                <div className="flex justify-end space-x-3">
                  <button
                    className="bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-2 px-4 rounded"
                    onClick={() => setShowEmailModal(false)}
                    disabled={emailNotificationStatus === 'sending'}
                  >
                    Cancel
                  </button>
                  <button
                    className="bg-primary hover:bg-primary-hover text-white font-semibold py-2 px-4 rounded disabled:opacity-60"
                    onClick={() => handleEmailNotification(notificationEmail)}
                    disabled={!notificationEmail || emailNotificationStatus === 'sending'}
                  >
                    {emailNotificationStatus === 'sending' ? 'Setting up...' : 'Set Notification'}
                  </button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}

        {/* Media Viewer Modal */}
        {viewerMedia && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-90">
            <button
              onClick={handleCloseViewerMedia}
              className="absolute top-4 right-4 z-10 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
              type="button"
              aria-label="Close viewer"
            >
              <X className="h-6 w-6" />
            </button>

            <div className="max-w-[90vw] max-h-[90vh] flex items-center justify-center">
              {viewerMedia.type === 'image' ? (
                <img
                  src={viewerMedia.src}
                  alt="Full size preview"
                  className="max-w-full max-h-[90vh] object-contain rounded"
                />
              ) : (
                <video
                  src={viewerMedia.src}
                  controls
                  autoPlay
                  playsInline
                  className="max-w-full max-h-[90vh] rounded bg-black"
                  onError={(e) => {
                    console.error('Video playback error in modal:', e);
                    setNotification({ message: 'Unable to play video', type: 'error' });
                    setTimeout(() => setNotification(null), 3000);
                  }}
                />
              )}
            </div>

            <div className="absolute inset-0 -z-10" onClick={handleCloseViewerMedia} />
          </div>
        )}

        {/* Image Modal */}
        {selectedImage && (
          <div
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black bg-opacity-75"
            onClick={handleCloseImageModal}
          >
            <div
              className="relative p-4 bg-white rounded-lg shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={selectedImage.src}
                alt={selectedImage.alt}
                className="max-w-screen-md max-h-[80vh] object-contain"
              />
              <button
                onClick={handleCloseImageModal}
                className="absolute -top-3 -right-3 text-white bg-gray-800 rounded-full h-8 w-8 flex items-center justify-center text-lg font-bold hover:bg-black"
                aria-label="Close image popup"
              >
                &times;
              </button>
            </div>
          </div>
        )}

        {/* Notification Toast */}
        {notification && (
          <div className={`fixed bottom-4 right-4 ${zIndexClasses.toast} animate-fade-in`}>
            <div
              className={`px-4 py-3 rounded-lg shadow-lg flex items-center gap-2 ${
                notification.type === 'success' ? 'bg-primary text-white' : 'bg-red-500 text-white'
              }`}
            >
              <span className="text-sm font-medium">{notification.message}</span>
            </div>
          </div>
        )}

        {/* Confirmation Dialog */}
        {confirmDialog && (
          <>
            <div
              className={`fixed inset-0 bg-black bg-opacity-50 ${zIndexClasses.maximum}`}
              onClick={() => setConfirmDialog(null)}
            />
            <div
              className={`fixed inset-0 flex items-center justify-center ${zIndexClasses.maximum} px-4`}
            >
              <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Confirm Action</h3>
                <p className="text-sm text-gray-600 mb-6">{confirmDialog.message}</p>
                <div className="flex gap-3 justify-end">
                  <button
                    onClick={() => setConfirmDialog(null)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmDialog.onConfirm}
                    className="px-4 py-2 text-sm font-medium text-white bg-red-500 hover:bg-red-600 rounded-lg transition-colors"
                  >
                    Confirm
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    );
  },
);

SimulationResultsContent.displayName = 'SimulationResultsContent';

export default SimulationResultsContent;
